import type { RegionData } from "@components/region-map-display";
import { formatCountyLabel } from "@consts/sales-network-pl";
import {
  CursorArrowRaysIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import type { Advisor, AdvisorNetwork } from "@type/contact";
import dynamic from "next/dynamic";
import Image from "next/image";
import useTranslation from "next-translate/useTranslation";
import {
  type ChangeEvent,
  type MouseEvent,
  useCallback,
  useId,
  useMemo,
  useState,
} from "react";

import { initials, isLightColor, telHref } from "./contact-utils";
import { SectionHeading } from "./section-heading";

const PolandMapDisplay = dynamic(() => import("../poland-map-display"), {
  ssr: false,
});

const GERMANY_MAP_SRC =
  "https://kns.you2.pl/wp-content/uploads/mapaDE-1123x1536.png";
const MIN_QUERY_LENGTH = 2;
const MAX_SEARCH_RESULTS = 6;
// Rdzeń nazwy miasta: "Rybnik" -> "rybn" pasuje do "rybnicki",
// "Żary" -> "zar" do "żarski", "Kraków" -> "krak" do "krakowski"
const MIN_STEM_LENGTH = 3;
const STEM_SUFFIX_LENGTH = 2;
const DIACRITICS_REGEX = /[̀-ͯ]/g;

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(DIACRITICS_REGEX, "")
    .replaceAll("ł", "l")
    .trim();

interface RegionMatch {
  advisor: Advisor;
  regionId: string;
}

const findRegions = (advisors: Advisor[], query: string): RegionMatch[] => {
  const normalizedQuery = normalize(query);
  if (normalizedQuery.length < MIN_QUERY_LENGTH) {
    return [];
  }
  const [firstWord] = normalizedQuery.split(" ");
  const stem = firstWord.slice(
    0,
    Math.max(MIN_STEM_LENGTH, firstWord.length - STEM_SUFFIX_LENGTH)
  );

  const matches: RegionMatch[] = [];
  for (const advisor of advisors) {
    for (const regionId of advisor.regionIds ?? []) {
      const name = normalize(formatCountyLabel(regionId));
      const regionName = name.replace("powiat ", "");
      if (name.includes(normalizedQuery) || regionName.startsWith(stem)) {
        matches.push({ advisor, regionId });
      }
    }
  }
  return matches.slice(0, MAX_SEARCH_RESULTS);
};

interface AdvisorFinderProps {
  network: AdvisorNetwork;
}

export const AdvisorFinder = ({ network }: AdvisorFinderProps) => {
  const { t } = useTranslation("contact");
  const searchId = useId();
  const [query, setQuery] = useState("");
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const { advisors, map } = network;
  const hasRegions = map === "pl";
  const results = useMemo(
    () => (hasRegions ? findRegions(advisors, query) : []),
    [advisors, hasRegions, query]
  );
  const regionAdvisor = selectedRegion
    ? advisors.find((advisor) => advisor.name === selectedName)
    : undefined;

  const handleMapSelect = useCallback((pathId: string, data: RegionData) => {
    setSelectedName(data.salesRepName);
    setSelectedRegion(pathId);
  }, []);

  const handleQueryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
      setShowResults(true);
    },
    []
  );

  // Dane wyniku w atrybutach data-*, aby handler był jeden i stabilny
  const handleResultSelect = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { advisor, region } = event.currentTarget.dataset;
      setSelectedName(advisor ?? null);
      setSelectedRegion(region ?? null);
      setShowResults(false);
    },
    []
  );

  const handleToggleRegion = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { advisor } = event.currentTarget.dataset;
      setSelectedRegion(null);
      setSelectedName((current) =>
        current === advisor ? null : (advisor ?? null)
      );
    },
    []
  );

  const showNoResults =
    hasRegions &&
    showResults &&
    normalize(query).length >= MIN_QUERY_LENGTH &&
    results.length === 0;

  return (
    <section
      aria-labelledby="advisors-title"
      className="mx-auto max-w-7xl scroll-mt-28 px-4 py-16 sm:px-6 md:py-24 lg:px-8"
      id="advisors"
    >
      <SectionHeading
        description={t(`advisors.${map}.description`)}
        eyebrow={t(`advisors.${map}.eyebrow`)}
        id="advisors-title"
        title={t(`advisors.${map}.title`)}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-8">
        <div className="flex flex-col rounded-2xl bg-[#f6f4ef] p-4 md:p-6">
          <div className="flex flex-1 items-center justify-center">
            {map === "pl" ? (
              <div className="w-full">
                <PolandMapDisplay
                  highlightedRepName={selectedName}
                  loadingText={t("advisors.loadingMap")}
                  onRegionSelect={handleMapSelect}
                  selectedPathId={selectedRegion}
                  showRepCards={false}
                />
              </div>
            ) : (
              <Image
                alt={t("advisors.de.mapAlt")}
                className="mx-auto h-auto w-full max-w-md"
                height={1536}
                src={GERMANY_MAP_SRC}
                width={1123}
              />
            )}
          </div>
          {hasRegions && (
            <p className="mt-4 inline-flex items-center gap-2 self-start rounded-full bg-white px-3 py-1.5 text-gray-600 text-xs shadow-sm">
              <CursorArrowRaysIcon
                aria-hidden="true"
                className="size-4 text-[#8a6d2f]"
              />
              {t("advisors.mapHint")}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {hasRegions && (
            <div>
              <label className="sr-only" htmlFor={searchId}>
                {t("advisors.searchLabel")}
              </label>
              <div className="relative">
                <MagnifyingGlassIcon
                  aria-hidden="true"
                  className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-gray-400"
                />
                <input
                  autoComplete="off"
                  className="w-full rounded-xl border border-gray-200 bg-white py-3.5 pr-4 pl-12 text-[#051124] text-sm shadow-sm outline-none transition placeholder:text-gray-400 focus:border-[#c1a166] focus:ring-4 focus:ring-[#c1a166]/15"
                  id={searchId}
                  onChange={handleQueryChange}
                  placeholder={t("advisors.searchPlaceholder")}
                  type="search"
                  value={query}
                />
              </div>
              {showResults && results.length > 0 && (
                <ul className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  {results.map(({ advisor, regionId }) => (
                    <li key={regionId}>
                      <button
                        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition hover:bg-[#f6f4ef] focus-visible:bg-[#f6f4ef] focus-visible:outline-none"
                        data-advisor={advisor.name}
                        data-region={regionId}
                        onClick={handleResultSelect}
                        type="button"
                      >
                        <span className="inline-flex items-center gap-2 text-[#051124]">
                          <MapPinIcon
                            aria-hidden="true"
                            className="size-4 text-gray-400"
                          />
                          {formatCountyLabel(regionId)}
                        </span>
                        <span className="text-gray-500 text-xs">
                          {advisor.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {showNoResults && (
                <p className="mt-2 px-1 text-gray-500 text-sm">
                  {t("advisors.noResults")}
                </p>
              )}
            </div>
          )}

          <div aria-live="polite">
            {regionAdvisor && selectedRegion && (
              <p className="rounded-xl border border-[#c1a166]/40 bg-[#c1a166]/10 px-4 py-3 text-[#051124] text-sm">
                {t("advisors.regionMatch", {
                  name: regionAdvisor.name,
                  region: formatCountyLabel(selectedRegion),
                })}
              </p>
            )}
          </div>

          <ul className="flex flex-col gap-3">
            {advisors.map((advisor) => (
              <AdvisorCard
                advisor={advisor}
                isSelected={advisor.name === selectedName}
                key={advisor.name}
                onToggleRegion={handleToggleRegion}
              />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

interface AdvisorCardProps {
  advisor: Advisor;
  isSelected: boolean;
  onToggleRegion: (event: MouseEvent<HTMLButtonElement>) => void;
}

const AdvisorCard = ({
  advisor,
  isSelected,
  onToggleRegion,
}: AdvisorCardProps) => {
  const { t } = useTranslation("contact");
  const hasRegion = Boolean(advisor.regionIds?.length);

  return (
    <li
      className={`flex flex-col gap-3 rounded-xl border p-4 transition sm:flex-row sm:items-center ${
        isSelected
          ? "border-[#051124] bg-[#051124] text-white shadow-lg"
          : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          aria-hidden="true"
          className={`flex size-10 shrink-0 items-center justify-center rounded-full font-bold text-sm ${
            isLightColor(advisor.color) ? "text-[#051124]" : "text-white"
          } ${isSelected ? "ring-2 ring-white/40" : ""}`}
          style={{ backgroundColor: advisor.color }}
        >
          {initials(advisor.name)}
        </span>
        <div className="min-w-0">
          <p className="truncate font-semibold text-sm">{advisor.name}</p>
          <p
            className={`truncate text-xs ${isSelected ? "text-white/60" : "text-gray-500"}`}
          >
            {advisor.role ?? t("advisors.defaultRole")}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-13 text-sm sm:flex-col sm:items-end sm:gap-0.5 sm:pl-0">
        <a
          className={`inline-flex items-center gap-1.5 whitespace-nowrap font-semibold transition ${
            isSelected
              ? "hover:text-[#c1a166]"
              : "text-[#051124] hover:text-[#8a6d2f]"
          }`}
          href={telHref(advisor.phone)}
        >
          <PhoneIcon aria-hidden="true" className="size-3.5 sm:hidden" />
          {advisor.phone}
        </a>
        {(isSelected || !hasRegion) && (
          <a
            className={`inline-flex items-center gap-1.5 break-all text-xs transition hover:underline ${
              isSelected ? "text-white/80" : "text-[#8a6d2f]"
            }`}
            href={`mailto:${advisor.email}`}
          >
            <EnvelopeIcon aria-hidden="true" className="size-3.5 sm:hidden" />
            {advisor.email}
          </a>
        )}
        {hasRegion && (
          <button
            aria-pressed={isSelected}
            className={`font-semibold text-xs transition hover:underline ${
              isSelected ? "text-[#c1a166]" : "text-[#8a6d2f]"
            }`}
            data-advisor={advisor.name}
            onClick={onToggleRegion}
            type="button"
          >
            {isSelected ? t("advisors.hideRegion") : t("advisors.showRegion")}
          </button>
        )}
      </div>
    </li>
  );
};
